import React, { useState } from 'react';
import { ArrowRight, CheckCircle, TrendingUp, Users, Clock, DollarSign, Star, BarChart3, Zap, Target, Bot, Building } from 'lucide-react';

const CaseStudiesPage = () => {
  const [activeCase, setActiveCase] = useState(0);

  const keyStats = [
    { metric: "94%", label: "Reduction in Human Errors", icon: <CheckCircle className="w-6 h-6" /> },
    { metric: "340%", label: "Average First Year ROI", icon: <TrendingUp className="w-6 h-6" /> },
    { metric: "200+", label: "Businesses Transformed", icon: <Users className="w-6 h-6" /> },
    { metric: "127", label: "Days Average Payback", icon: <Clock className="w-6 h-6" /> }
  ];

  const caseStudies = [
    {
      company: "TechStart Inc",
      industry: "SaaS • 50 employees",
      challenge: "Customer support team drowning in 200+ daily tickets, 12-hour response times killing conversions",
      solution: "Claude-powered support bot with product knowledge base and intelligent escalation system",
      implementation: "2 weeks development, 1 week training, gradual 20% → 80% rollout",
      results: {
        primary: "40% Revenue Increase",
        metrics: [
          { label: "Response Time", before: "12 hours", after: "30 seconds", improvement: "99.7% faster" },
          { label: "Customer Satisfaction", before: "3.4/5", after: "4.7/5", improvement: "38% increase" },
          { label: "Support Costs", before: "$89,000/year", after: "$31,000/year", improvement: "$58,000 saved" },
          { label: "Automation Rate", before: "0%", after: "80%", improvement: "24/7 coverage" }
        ]
      },
      testimonial: "Our support team went from drowning in tickets to focusing on complex issues that actually need human expertise. Revenue jumped 40% because we stopped losing frustrated customers.",
      author: "Sarah Chen, Founder"
    },
    {
      company: "Growth Marketing Co",
      industry: "Digital Marketing Agency • 25 employees",
      challenge: "Losing clients to competitors who published content faster, brand voice inconsistency across writers",
      solution: "AI competitor monitoring system + content creation pipeline with brand voice training",
      implementation: "3 weeks setup, competitor analysis, brand voice modeling, content workflow automation",
      results: {
        primary: "85% Client Retention Boost",
        metrics: [
          { label: "Content Output", before: "2-3 posts/week", after: "12-15 posts/week", improvement: "400% increase" },
          { label: "Brand Voice Consistency", before: "64%", after: "97%", improvement: "52% improvement" },
          { label: "Client Onboarding Time", before: "6 weeks", after: "2.4 weeks", improvement: "60% faster" },
          { label: "New Client Acquisition", before: "1-2/month", after: "5-6/month", improvement: "3x growth" }
        ]
      },
      testimonial: "BragAI's system knows our competitors better than we do. We're always first to market with trends, and our content quality is so consistent that clients think we have a team of 20 writers.",
      author: "Mike Rodriguez, CEO"
    },
    {
      company: "E-commerce Dynamics",
      industry: "Online Retail • 30 employees", 
      challenge: "Manual lead qualification taking 15 hours/week, missing hot prospects, poor follow-up consistency",
      solution: "AI lead generation bots + smart Airtable CRM integration with automated follow-up sequences",
      implementation: "4 weeks: bot development, CRM integration, lead scoring system, automated workflows",
      results: {
        primary: "$127,000 Additional Revenue",
        metrics: [
          { label: "Lead Volume", before: "50/week", after: "200/week", improvement: "300% increase" },
          { label: "Qualification Time", before: "15 hours/week", after: "2 hours/week", improvement: "87% reduction" },
          { label: "Conversion Rate", before: "12%", after: "18.4%", improvement: "53% improvement" },
          { label: "Hot Lead Loss", before: "32%", after: "8%", improvement: "75% reduction" }
        ]
      },
      testimonial: "We went from manually chasing leads to having a system that never sleeps. Our conversion rates shot up because every lead gets perfect, immediate attention.",
      author: "Jennifer Walsh, Sales Director"
    }
  ];

  const benefits = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Perfect Consistency",
      description: "AI systems deliver identical quality every time - no bad days, no human error, no inconsistency"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "24/7 Operation", 
      description: "Your business never sleeps. AI handles tasks instantly, weekends, holidays, and after hours"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Scalable Growth",
      description: "Handle 10x the volume without hiring more staff. Scale up or down instantly based on demand"
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Massive ROI",
      description: "Average 340% first-year ROI with 127-day payback period across 200+ implementations"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              BragAI
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-300 hover:text-blue-400 transition-colors">Home</a>
              <a href="/use-cases" className="text-gray-300 hover:text-blue-400 transition-colors">Use Cases</a>
              <a href="/ai-discovery" className="text-gray-300 hover:text-purple-400 transition-colors">AI Discovery</a>
              <a href="/case-studies" className="text-blue-400">Case Studies</a>
              <a href="/#contact" className="text-gray-300 hover:text-blue-400 transition-colors">Contact</a>
            </div>
            <button 
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Book Call
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-600/20 px-4 py-2 rounded-full mb-6">
            <BarChart3 className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">Real Results • Real Businesses</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
            <span className="block text-gray-100">How AI Automation</span>
            <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Transforms Businesses
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            See exactly how 200+ businesses eliminated manual tasks, reduced errors by 94%, 
            and achieved an average 340% ROI in their first year with AI automation.
          </p>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {keyStats.map((stat, index) => (
              <div key={index} className="text-center hover:scale-105 transition-transform duration-300">
                <div className="text-blue-400 mb-3 flex justify-center">{stat.icon}</div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.metric}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why AI Automation Works */}
      <section className="py-20 px-6 bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Why AI Automation Delivers Superior Results</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Unlike human-dependent processes, AI systems provide perfect consistency, eliminate errors, and scale infinitely
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 text-center hover:bg-gray-750 transition-all duration-300 hover:scale-105">
                <div className="text-blue-400 mb-4 flex justify-center">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Case Studies */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">Detailed Success Stories</h2>
          
          {/* Case Study Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {caseStudies.map((study, index) => (
              <button
                key={index}
                onClick={() => setActiveCase(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  activeCase === index 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {study.company}
              </button>
            ))}
          </div>

          {/* Active Case Study */}
          <div className="bg-gray-800 rounded-2xl p-8 mb-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column - Problem & Solution */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center">
                    <Building className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{caseStudies[activeCase].company}</h3>
                    <p className="text-blue-400">{caseStudies[activeCase].industry}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3">Challenge</h4>
                    <p className="text-gray-300">{caseStudies[activeCase].challenge}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3">Solution</h4>
                    <p className="text-gray-300">{caseStudies[activeCase].solution}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-blue-400 mb-3">Implementation</h4>
                    <p className="text-gray-300">{caseStudies[activeCase].implementation}</p>
                  </div>
                </div>
              </div>

              {/* Right Column - Results */}
              <div>
                <div className="text-center mb-8">
                  <div className="text-4xl font-bold text-green-400 mb-2">
                    {caseStudies[activeCase].results.primary}
                  </div>
                  <p className="text-gray-400">Primary Business Impact</p>
                </div>

                <div className="space-y-4 mb-8">
                  {caseStudies[activeCase].results.metrics.map((metric, index) => (
                    <div key={index} className="bg-gray-900 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-white">{metric.label}</span>
                        <span className="text-green-400 font-medium">{metric.improvement}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-red-300">Before: {metric.before}</span>
                        <span className="text-green-300">After: {metric.after}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Testimonial */}
                <div className="bg-blue-600/10 border border-blue-600/20 rounded-lg p-6">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-300 italic mb-4">"{caseStudies[activeCase].testimonial}"</p>
                  <div className="text-blue-400 font-semibold">{caseStudies[activeCase].author}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Breakdown */}
      <section className="py-20 px-6 bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">The Numbers Don't Lie</h2>
            <p className="text-xl text-gray-300">Average ROI across 200+ AI automation implementations</p>
          </div>

          <div className="bg-gradient-to-r from-green-600/10 to-blue-600/10 rounded-2xl p-12">
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <div className="text-5xl font-bold text-green-400 mb-4">340%</div>
                <div className="text-xl text-white mb-2">First Year ROI</div>
                <div className="text-gray-400">Including all implementation costs</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-blue-400 mb-4">127</div>
                <div className="text-xl text-white mb-2">Days to Break Even</div>
                <div className="text-gray-400">Average payback period</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-purple-400 mb-4">$180k</div>
                <div className="text-xl text-white mb-2">Annual Savings</div>
                <div className="text-gray-400">For mid-size businesses</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready for Results Like These?</h2>
          <p className="text-xl text-gray-300 mb-12">
            Every business is unique. Book a free strategy call to discover exactly how AI automation 
            can transform your specific processes and deliver measurable results.
          </p>
          
          <div className="space-y-8">
            <button 
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 px-12 py-4 rounded-lg font-medium text-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/25"
            >
              Book Your FREE 30min Strategy Call
            </button>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">Custom automation roadmap</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">ROI projections for your business</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">No sales pitch, just insights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">
            BragAI
          </div>
          <p className="text-gray-400 mb-6">
            AI Automation Services • Transforming Businesses with Intelligent Solutions
          </p>
          <div className="text-gray-500">
            © 2024 BragAI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaseStudiesPage;
